import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Stack } from '@astryxdesign/core/Stack'
import { Card } from '@astryxdesign/core/Card'
import { Text } from '@astryxdesign/core/Text'
import { Badge } from '@astryxdesign/core/Badge'
import { Divider } from '@astryxdesign/core/Divider'
import { Carousel } from '@astryxdesign/core/Carousel'
import { List, ListItem } from '@astryxdesign/core/List'
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl'
import { useBookings, useDays, useItems, usePlaces, useTrip } from '#/lib/hooks'
import { ActivityDocs, ActivityLabel, ActivityMeta } from '#/components/ActivityRow'
import { formatMinor } from '#/lib/money'
import type { Item } from '#/lib/types'

export const Route = createFileRoute('/trip')({ component: TripScreen })

type View = 'days' | 'places'

function TripScreen() {
  const trip = useTrip()
  const places = usePlaces(trip?.id)
  const items = useItems(trip?.id)
  const bookings = useBookings(trip?.id)
  const days = useDays(trip?.id)
  // Days first: on the trip itself you think in days, and a swipe is cheaper
  // than a tap plus a scroll. Places stays one switch away, for the question
  // days cannot answer — what do we actually do in Jaisalmer.
  const [view, setView] = useState<View>('days')

  if (!trip) return <Text type="supporting">No trip yet.</Text>

  const unbooked = bookings.filter((b) => b.status === 'unbooked')
  const waitlisted = bookings.filter((b) => b.status === 'waitlisted')
  const label = (b: (typeof bookings)[number]) =>
    b.from_loc && b.to_loc ? `${b.from_loc} → ${b.to_loc}` : b.vendor ?? b.kind

  return (
    <Stack direction="vertical" gap={4}>
      <Stack direction="vertical" gap={0}>
        <Text type="display-3" display="block">{trip.name}</Text>
        <Text type="supporting">
          {fmtDate(trip.start_date)} – {fmtDate(trip.end_date)} · {places.length} places
          {trip.country ? ` · ${trip.country}` : ''}
        </Text>
        {trip.summary && <Text type="supporting">{trip.summary}</Text>}
      </Stack>

      {/* A waitlisted train is not a task like an unbooked hotel — it is a seat
          you may not have, so it reads louder and sits first. */}
      {waitlisted.length > 0 && (
        <Card variant="red">
          <Stack direction="vertical" gap={1}>
            <Text type="label" weight="semibold">Waitlisted — not confirmed</Text>
            {waitlisted.map((b) => <Text key={b.id} type="supporting">{label(b)}</Text>)}
            <Text type="supporting">
              A fully waitlisted e-ticket is auto-cancelled at chart preparation.
              Hold a confirmed backup.
            </Text>
          </Stack>
        </Card>
      )}

      {unbooked.length > 0 && (
        <Card variant="muted">
          <Stack direction="vertical" gap={1}>
            <Text type="label" color="secondary">Still to book ({unbooked.length})</Text>
            {unbooked.slice(0, 4).map((b) => (
              <Text key={b.id} type="supporting">{label(b)}</Text>
            ))}
            {unbooked.length > 4 && (
              <Text type="supporting">and {unbooked.length - 4} more</Text>
            )}
          </Stack>
        </Card>
      )}

      <SegmentedControl
        label="Itinerary view"
        value={view}
        onChange={(v) => setView(v as View)}
        layout="fill"
       
      >
        <SegmentedControlItem value="days" label="Days" />
        <SegmentedControlItem value="places" label="Places" />
      </SegmentedControl>

      {view === 'days'
        ? <DayCarousel days={days} items={items} places={places} currency={trip.base_currency} />
        : <PlaceList
            places={places}
            items={items}
            bookings={bookings}
            currency={trip.base_currency}
          />}
    </Stack>
  )
}

/**
 * Default view: one full-width day per swipe.
 *
 * No peeking second card and no "+N more": a day you have to tap to finish
 * reading is a day you do not read. One card fills the width, lists everything
 * on that day, and the dots below say how far through the trip you are.
 */
function DayCarousel({
  days, items, places, currency,
}: {
  days: string[]
  items: Item[]
  places: ReturnType<typeof usePlaces>
  currency: string
}) {
  const [active, setActive] = useState(0)

  if (!days.length) return <Text type="supporting">No days planned yet.</Text>

  // Derived from scroll position rather than a click handler, so dragging,
  // flicking and the arrow buttons all keep the dots honest.
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement
    if (!el || typeof el.scrollLeft !== 'number' || !el.clientWidth) return
    const i = Math.round(el.scrollLeft / el.clientWidth)
    if (i !== active && i >= 0 && i < days.length) setActive(i)
  }

  return (
    <Stack direction="vertical" gap={2}>
      {/* Sticky, and sized to the space actually available so it never runs
          past the viewport. Once stuck, a scroll over the card scrolls the
          card's own list rather than the page behind it. */}
      <div className="day-pager" onScrollCapture={onScroll}>
        <Carousel aria-label="Days" hasSnap hasEdgeFade={false} gap={0} padding={0}>
          {days.map((day, i) => {
            const own = items
              .filter((it) => it.day_date === day)
              .sort((a, b) => (a.starts_at ?? Infinity) - (b.starts_at ?? Infinity))
            const place = places.find((p) => p.id === own[0]?.place_id)
            const end = places.find((p) => p.id === own[own.length - 1]?.place_id)
            const spend = own.reduce((s, it) => s + (it.price_minor ?? 0), 0)
            const tickets = own.filter((it) => it.ticket_status === 'todo').length

            return (
              // The carousel centres its items, so a row sized by the longest day
              // leaves shorter days floating in a void. Equal heights fix it: 620
              // fits nine activities at phone width, and the two longest days
              // scroll a little inside the card — a far smaller ask than "+7 more".
              <Card key={day} width="100%" className="day-pager-card">
                <Stack direction="vertical" gap={3} height="100%" justify="start">
                  <Stack direction="vertical" gap={0}>
                    <Text type="supporting">Day {i + 1} of {days.length} · {fmtDate(day)}</Text>
                    <Text type="large" weight="semibold" display="block">
                      {place && end && place.id !== end.id
                        ? `${place.name} → ${end.name}`
                        : place?.name ?? 'Unplanned'}
                    </Text>
                  </Stack>

                  <Stack direction="horizontal" gap={1} wrap="wrap">
                    <Badge
                      label={`${own.length} ${own.length === 1 ? 'thing' : 'things'}`}
                      variant="neutral"
                    />
                    {spend > 0 && (
                      <Badge label={`~${formatMinor(spend, currency)}`} variant="teal" />
                    )}
                    {tickets > 0 && (
                      <Badge
                        label={`${tickets} ${tickets === 1 ? 'ticket' : 'tickets'}`}
                        variant="orange"
                      />
                    )}
                  </Stack>

                  <Divider />

                  {/* Every activity, in full. Nothing to expand. */}
                  <Stack direction="vertical" isScrollable height="100%" className="day-pager-scroll">
                    <List hasDividers density="compact">
                    {own.map((it) => (
                      <ListItem
                        key={it.id}
                        label={<ActivityLabel item={it} />}
                        description={
                          <Stack direction="vertical" gap={1}>
                            {it.short_description && (
                              <Text type="supporting">{it.short_description}</Text>
                            )}
                            <ActivityMeta item={it} />
                            <ActivityDocs item={it} />
                          </Stack>
                        }
                      />
                      ))}
                    </List>
                  </Stack>
                </Stack>
              </Card>
            )
          })}
        </Carousel>
      </div>

      {/* The indicator: enough to say "this swipes", without stealing a card.
          Typographic rather than eleven Badges, which read as controls. */}
      <Stack direction="horizontal" gap={2} justify="center" align="center">
        <Text type="supporting" justify="center">
          {days.map((_, i) => (i === active ? '●' : '○')).join(' ')}
        </Text>
      </Stack>
    </Stack>
  )
}

/**
 * The other view: the whole trip laid out by place, nothing folded away.
 *
 * This was an accordion, which answered the wrong question. "What do we do in
 * Jaisalmer" was three taps and a memory test — open the stop, read, close it,
 * open the next — and the one thing you actually want, how the eleven days are
 * distributed across seven places, was never on screen at once.
 *
 * So: a contents card that shows the shape of the trip in one glance, then
 * every place in full underneath. Long, and meant to be. Scrolling is cheaper
 * than remembering.
 */
function PlaceList({
  places, items, bookings, currency,
}: {
  places: ReturnType<typeof usePlaces>
  items: Item[]
  bookings: ReturnType<typeof useBookings>
  currency: string
}) {
  if (!places.length) return <Text type="supporting">No places yet.</Text>

  const forPlace = (id: string) =>
    items
      .filter((i) => i.place_id === id)
      .sort((a, b) => (a.starts_at ?? Infinity) - (b.starts_at ?? Infinity) || a.sort_order - b.sort_order)

  // Activities whose place was deleted would otherwise vanish from this view
  // entirely — visible under Days, missing here, with nothing to say why.
  const known = new Set(places.map((p) => p.id))
  const orphans = items
    .filter((i) => !i.place_id || !known.has(i.place_id))
    .sort((a, b) => (a.starts_at ?? Infinity) - (b.starts_at ?? Infinity))

  const jump = (id: string) => {
    document.getElementById(`place-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <Stack direction="vertical" gap={4}>
      {/* The distribution itself: how long, and how much, in each place. The
          rows jump to the section rather than expanding it, so this reads as
          a contents page and not another thing to operate. */}
      <Card>
        <Stack direction="vertical" gap={2}>
          <Text type="label" color="secondary">Where the trip goes</Text>
          <List hasDividers density="compact">
            {places.map((p) => {
              const own = forPlace(p.id)
              return (
                <ListItem
                  key={p.id}
                  label={p.name}
                  description={`${nightsLabel(p.arrive_date, p.depart_date)} · ${fmtDate(p.arrive_date)}`}
                  onClick={() => jump(p.id)}
                  endContent={
                    <Text type="supporting" hasTabularNumbers>
                      {own.length} {own.length === 1 ? 'thing' : 'things'}
                    </Text>
                  }
                />
              )
            })}
          </List>
        </Stack>
      </Card>

      {places.map((p) => {
        const own = forPlace(p.id)
        const stay = bookings.filter((b) => b.place_id === p.id)
        const spend = own.reduce((s, i) => s + (i.price_minor ?? 0), 0)
        const tickets = own.filter((i) => i.ticket_status === 'todo').length
        const keyThings = own.filter((i) => i.is_key).length

        return (
          <div key={p.id} id={`place-${p.id}`}>
            <Card>
              <Stack direction="vertical" gap={3}>
                {/* Sticky: Jaisalmer is twenty-five things long, and eight of
                    them in you can no longer see which place you are reading. */}
                <div className="place-head">
                  <Stack direction="vertical" gap={0}>
                    <Text type="large" weight="semibold" display="block">{p.name}</Text>
                    <Text type="supporting">
                      {fmtDate(p.arrive_date)} – {fmtDate(p.depart_date)}
                      {' · '}{nightsLabel(p.arrive_date, p.depart_date)}
                    </Text>
                  </Stack>
                </div>

                <Stack direction="horizontal" gap={1} wrap="wrap">
                  <Badge
                    label={`${own.length} ${own.length === 1 ? 'thing' : 'things'}`}
                    variant="neutral"
                  />
                  {keyThings > 0 && <Badge label={`${keyThings} key`} variant="purple" />}
                  {spend > 0 && <Badge label={`~${formatMinor(spend, currency)}`} variant="teal" />}
                  {tickets > 0 && (
                    <Badge
                      label={`${tickets} ${tickets === 1 ? 'ticket' : 'tickets'} to book`}
                      variant="orange"
                    />
                  )}
                </Stack>

                {stay.length > 0 && (
                  <>
                    <Divider />
                    <List density="compact">
                      {stay.map((b) => (
                        <ListItem
                          key={b.id}
                          label={b.vendor ?? b.kind}
                          description={b.ref ?? undefined}
                          startContent={<Badge label={b.status} variant={
                            b.status === 'booked' ? 'green'
                            : b.status === 'waitlisted' ? 'red' : 'neutral'
                          } />}
                          endContent={b.cost_minor != null
                            ? <Text type="supporting" hasTabularNumbers>
                                {formatMinor(b.cost_minor, b.currency)}
                              </Text>
                            : undefined}
                        />
                      ))}
                    </List>
                  </>
                )}

                <Divider />
                {own.length === 0
                  ? <Text type="supporting">Nothing planned here yet.</Text>
                  : <ByDay items={own} />}
              </Stack>
            </Card>
          </div>
        )
      })}

      {orphans.length > 0 && (
        <Card variant="muted">
          <Stack direction="vertical" gap={3}>
            <Stack direction="vertical" gap={0}>
              <Text type="large" weight="semibold" display="block">Not tied to a place</Text>
              <Text type="supporting">
                These still show up on their day. They are here so nothing is
                only visible in one of the two views.
              </Text>
            </Stack>
            <ByDay items={orphans} />
          </Stack>
        </Card>
      )}
    </Stack>
  )
}

/**
 * A place usually spans two or three days, and losing that would turn "three
 * days in Jaisalmer" into one undifferentiated list of twenty things. The day
 * is a quiet heading rather than a control: still one page, still no tapping.
 */
function ByDay({ items }: { items: Item[] }) {
  const groups: Array<[string, Item[]]> = []
  for (const i of items) {
    const day = i.day_date ?? ''
    const last = groups[groups.length - 1]
    if (last && last[0] === day) last[1].push(i)
    else groups.push([day, [i]])
  }

  return (
    <Stack direction="vertical" gap={3}>
      {groups.map(([day, list]) => (
        <Stack key={day || 'undated'} direction="vertical" gap={1}>
          <Text type="label" color="secondary">{day ? fmtDate(day) : 'No date'}</Text>
          <List hasDividers density="compact">
            {list.map((i) => (
              <ListItem
                key={i.id}
                label={<ActivityLabel item={i} />}
                description={
                  <Stack direction="vertical" gap={1}>
                    {i.short_description && (
                      <Text type="supporting">{i.short_description}</Text>
                    )}
                    <ActivityMeta item={i} />
                    <ActivityDocs item={i} />
                  </Stack>
                }
              />
            ))}
          </List>
        </Stack>
      ))}
    </Stack>
  )
}

/** "2 nights", or "day stop" when you arrive and leave the same day. */
function nightsLabel(arrive: string, depart: string): string {
  const a = new Date(`${arrive}T00:00:00`).getTime()
  const d = new Date(`${depart}T00:00:00`).getTime()
  if (Number.isNaN(a) || Number.isNaN(d)) return '—'
  const nights = Math.round((d - a) / 864e5)
  if (nights <= 0) return 'day stop'
  return `${nights} ${nights === 1 ? 'night' : 'nights'}`
}

const fmtDate = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
}
